/**
 * AuthRestore — landing page for owner-minted one-time restore URLs.
 * Exchanges the URL's `?token=` for a real session_token via /api/auth/restore,
 * stores it via the AuthContext token store, then redirects to the right
 * place based on the user's role.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, AlertTriangle, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthRestore() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { refreshUser } = useAuth();
    const token = params.get("token");
    const [status, setStatus] = useState("loading");
    const [error, setError] = useState(null);
    const ran = useRef(false);

    useEffect(() => {
        if (ran.current) return;
        ran.current = true;
        if (!token) { setStatus("error"); setError("Missing token in URL"); return; }
        (async () => {
            try {
                const r = await axios.post(`${API}/auth/restore`, { token }, { withCredentials: true });
                if (r.data?.session_token) {
                    try { window.localStorage.setItem("maxsc_session_token", r.data.session_token); } catch { /* noop */ }
                }
                await refreshUser();
                setStatus("ok");
                const role = r.data?.user?.role;
                const dest = role === "owner" || role === "coach" ? "/coach" : "/dashboard";
                setTimeout(() => navigate(dest, { replace: true }), 800);
            } catch (e) {
                setStatus("error");
                setError(e?.response?.data?.detail || e.message || "Restore failed");
            }
        })();
    }, [token, navigate, refreshUser]);

    return (
        <div data-testid="auth-restore" className="min-h-screen bg-brand-obsidian text-white flex flex-col items-center justify-center px-6">
            {status === "loading" && (
                <>
                    <Loader2 className="w-8 h-8 text-brand-red animate-spin" />
                    <div className="mt-5 text-[10px] tracking-[0.3em] uppercase text-zinc-300">Restoring your session…</div>
                </>
            )}
            {status === "ok" && (
                <>
                    <CheckCircle2 className="w-12 h-12 text-emerald-400" />
                    <div className="mt-4 text-base text-white">You're signed in. Redirecting…</div>
                </>
            )}
            {status === "error" && (
                <>
                    <AlertTriangle className="w-10 h-10 text-amber-400 mb-3" />
                    <div className="text-[10px] tracking-[0.3em] uppercase text-amber-400 mb-2">Restore link didn't work</div>
                    <p className="text-sm text-zinc-300 max-w-md text-center mb-4" data-testid="auth-restore-error">{error}</p>
                    <Link to="/auth/diagnose" className="btn-ghost-tactical text-xs inline-flex items-center gap-2">
                        <ArrowLeft className="w-3.5 h-3.5" /> Run a diagnostic instead
                    </Link>
                </>
            )}
        </div>
    );
}
